import React, { createContext, useCallback } from 'react';
import _get from 'lodash/get';

import { useMutation, useToggle, useSegment } from 'utils/hooks';

/**
 * @type {React.Context<{
 *  isActive: boolean,
 *  isProcessing: boolean,
 *  onToggle: () => void
 * }>}
 */
const RequestReasonToggleContext = createContext();

export const useRequestReasonToggle = () => {
  const context = React.useContext(RequestReasonToggleContext);
  if (!context) {
    throw new Error(
      'useRequestReasonToggle must be used within a RequestReasonToggleProvider'
    );
  }
  return context;
};

export function RequestReasonToggleProvider({
  reason = null,
  onSaved = () => {},
  children = null
}) {
  const { track } = useSegment();

  if (!reason) {
    throw new Error('RequestReasonToggleProvider requires the reason prop');
  }

  const reasonId = _get(reason, 'id', '');
  const isEnabled = _get(reason, 'is_enabled', false);
  const newState = isEnabled ? 'disable' : 'enable';
  const reasonName = _get(reason, 'reason_name', '');

  const [isActive, toggle] = useToggle(isEnabled);

  const { mutate, isProcessing } = useMutation(
    'configure.reasons.root',
    'PUT',
    {
      routeParam: reasonId,
      payload: { ...reason, is_enabled: !isEnabled },
      errorMessage: `Failed to ${newState} the request reason.`,
      onError: () => toggle(),
      onSuccess: data => {
        track('FTU Wizard Return Reason Toggled', {
          reason_id: reasonId,
          reason_name: reasonName,
          is_enabled: !isEnabled
        });
        onSaved(data);
      }
    }
  );

  const onToggle = useCallback(() => {
    mutate();
    toggle();
  }, [mutate, toggle]);

  return (
    <RequestReasonToggleContext.Provider
      value={{
        isActive,
        isProcessing,
        onToggle
      }}
    >
      {children}
    </RequestReasonToggleContext.Provider>
  );
}
